import Container from '@/components/Container';
import styled, { keyframes } from 'styled-components';

/** Figma 20369:4757 — card 256 × 128, 16px apart, logo boxed at 224 × 96 with a 72px cap. */
export const PARTNER_CARD_WIDTH = 256;
export const PARTNER_CARD_HEIGHT = 128;
export const PARTNER_LOGO_MAX_HEIGHT = 72;
/**
 * Card gap, in px rather than via `theme.spacing`, because the seam maths below depends on it:
 * every card occupies exactly CARD_WIDTH + PARTNER_CARD_GAP, so half the doubled track is exactly one lap.
 * A flex `gap` cannot be used for the same reason — it is dropped after the last item, which would
 * make the two halves unequal and let the loop drift.
 */
export const PARTNER_CARD_GAP = 16;

/** Seconds per card. A whole lap therefore takes as long as the list is long. */
const SECONDS_PER_CARD = 3;

export const PartnersSection = styled.section(({ theme }) => ({
  backgroundColor: theme.colors.surface02,
  // The design pads this band 40/40 on mobile and 40/64 on desktop — deliberately asymmetric,
  // giving the strip room to breathe above the section that follows.
  paddingTop: theme.spacing[40],
  paddingBottom: theme.spacing[40],

  [`${theme.mediaBreakpointUp.md}`]: {
    paddingBottom: theme.spacing[64],
  },
}));

/**
 * The strip runs the full width of the window, so one lap has to span the widest window it is
 * likely to meet — 2560px (QHD). A wider window would see the lap end before it resets.
 */
export const PARTNER_MIN_LAP_WIDTH = 2560;

export const PartnersContainer = styled(Container)(({ theme }) => ({
  padding: `0 ${theme.spacing[20]}`,
}));

/**
 * Viewport for the marquee: the full width of the window, so the strip runs edge to edge and is
 * clipped by the window itself rather than by the content column.
 */
export const LogoViewport = styled.div(({ theme }) => ({
  overflow: 'hidden',
  // The cards carry a shadow that would be clipped flush against the viewport edge.
  padding: `${theme.spacing[4]} 0`,

  // With the animation off the strip still overflows, so it becomes an ordinary scroller.
  '@media (prefers-reduced-motion: reduce)': {
    overflowX: 'auto',
  },
}));

/**
 * One lap moves the track left by exactly half its width. The list is rendered twice, so the
 * moment the first copy has passed, the second is sitting where the first began and the reset is
 * invisible.
 */
const scrollLeft = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

// Template-literal syntax, not the object form used elsewhere in this folder: styled-components
// refuses to interpolate a `keyframes` value into an untagged string.
export const LogoTrack = styled.div<{ $cardCount: number }>`
  display: flex;
  width: max-content;
  animation: ${scrollLeft} ${({ $cardCount }) => $cardCount * SECONDS_PER_CARD}s linear infinite;

  &:hover {
    animation-play-state: paused;
  }

  /* Continuous motion is a migraine and vestibular trigger, and this strip carries no information
     that is lost by holding still. */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const LogoCard = styled.div(({ theme }) => ({
  flex: `0 0 ${PARTNER_CARD_WIDTH}px`,
  height: PARTNER_CARD_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing[16],
  borderRadius: theme.spacing[4],
  backgroundColor: theme.colors.highEmphasisWhite,
  boxShadow: theme.elevation[2],
  marginRight: PARTNER_CARD_GAP,
}));

export const LogoImage = styled.img({
  maxWidth: '100%',
  maxHeight: PARTNER_LOGO_MAX_HEIGHT,
  // Partner wordmarks arrive at every aspect ratio there is; contain keeps each one whole.
  objectFit: 'contain',
});
